import { useMemo } from "react";
import {
  getThemeLayout,
  getThemeStyles,
  getColors,
} from "../../utils/themeUtils";
import { DEFAULT_BRAND } from "../../theme/brandTheme";
import { Instagram, Facebook } from "react-feather";
import TikTokIcon from "../../assets/tiktok";
import { DiscordIcon } from "../../assets/discord";
import { formatstorageUrl } from "@/utils/formatters";

interface SimplifiedBrandPreviewProps {
  companyName: string;
  brandProfileImage?: string;
  niches: Array<{ id: string; name: string }>;
}

export function SimplifiedBrandPreview({
  companyName,
  brandProfileImage,
  niches,
}: SimplifiedBrandPreviewProps) {
  // Default appearance settings
  const defaultAppearanceSettings = {
    theme: "TYPE_1",
    primayColour: DEFAULT_BRAND.primary,
    secondaryColour: DEFAULT_BRAND.secondary,
    socialMediaIconSizes: "medium",
  };

  // Get colors from appearance settings
  const colors = useMemo(() => {
    return getColors(defaultAppearanceSettings);
  }, []);

  // Get theme-specific layout and styles
  const themeLayout = useMemo(() => {
    return getThemeLayout("TYPE_2");
  }, []);

  const themeStyles = useMemo(() => {
    return getThemeStyles("TYPE_1", colors);
  }, [colors]);

  return (
    <div
      className="min-h-screen w-full transition-all duration-500"
      style={{
        backgroundColor: `${colors.secondary}05`,
        transformOrigin: "top center",
      }}
    >
      {/* Banner Section */}
      <div className="relative">
        <div className={`relative h-32`}>
          {/* Banner Background */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, ${colors.secondary}, white)`,
            }}
          />
        </div>
      </div>

      <main className={themeLayout.container}>
        {/* Profile Section */}
        <div
          className={`${themeLayout.profileSection} ${themeLayout.profilePosition} translate-x-3`}
        >
          {/* Profile Image */}
          <div className={`${themeLayout.profileImage}`}>
            <div className={themeLayout.profileImageSize}>
              <div
                className={`w-full h-full rounded-full shadow-lg transition-all duration-300`}
                style={{
                  backgroundColor: brandProfileImage ? "white" : colors.primary,
                }}
              >
                {brandProfileImage ? (
                  <img
                    src={ formatstorageUrl(brandProfileImage) }
                    alt={companyName || "company logo"}
                    className={`w-full h-full rounded-full object-cover transition-all duration-300`}
                  />
                ) : (
                  <div
                    className={`w-full h-full rounded-full flex items-center justify-center text-white font-bold`}
                    style={{ backgroundColor: colors.primary }}
                  >
                    {(companyName || "")
                      .split(" ")
                      .map((name) => name.charAt(0))
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className={`${themeLayout.profileInfo}`}>
            {/* Company Name and Niches */}
            <div className={`flex flex-col sm:flex-row sm:items-center gap-4`}>
              <h1
                className={`text-base text-center pr-8 font-semibold text-foreground`}
              >
                {companyName || "Your Brand"}
              </h1>
              <div className="flex flex-wrap gap-2 justify-center -translate-x-2 sm:justify-start items-center">
                {niches.map((niche) => (
                  <span
                    key={niche.id}
                    className={`${themeStyles.nicheTag} transition-all duration-300 text-xs`}
                    style={{
                      backgroundColor: `${colors.primary}20`,
                      color: colors.primary,
                    }}
                  >
                    {niche.name}
                  </span>
                ))}
                {/* Social Media Icons */}
              </div>
              <div className="flex items-center gap-2 ml-2">
                socials:
                <div className={`${themeStyles.socialIcon}`} title="Instagram">
                  <Instagram className="w-5 h-5" />
                </div>
                <div className={`${themeStyles.socialIcon}`} title="TikTok">
                  <TikTokIcon className="w-5 h-5" />
                </div>
                <div className={`${themeStyles.socialIcon}`} title="Facebook">
                  <Facebook className="w-5 h-5" />
                </div>
                <div className={`${themeStyles.socialIcon}`} title="Discord">
                  <DiscordIcon className="w-5 h-5" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className={themeLayout.contentGrid}>
          {/* Main Content */}
          <div
            className={`${themeLayout.description} ${themeLayout.statsPosition}`}
          >
            {/* Stats */}
            <div className={themeStyles.statsContainer}>
              <div className={`${themeLayout.statsGrid} gap-2`}>
                <div
                  className={`${themeStyles.statsCard} transition-all duration-300`}
                >
                  <div
                    className={`text-lg font-semibold mb-1`}
                    style={{ color: colors.primary }}
                  >
                    #1
                  </div>
                  <div className="text-xs text-muted-foreground">Rank</div>
                </div>
                <div
                  className={`${themeStyles.statsCard} transition-all duration-300`}
                >
                  <div
                    className={`text-lg font-semibold mb-1`}
                    style={{ color: colors.primary }}
                  >
                    126
                  </div>
                  <div className="text-xs text-muted-foreground">Products</div>
                </div>
                <div
                  className={`${themeStyles.statsCard} transition-all duration-300`}
                >
                  <div className={`text-lg font-semibold mb-1`}>1k</div>
                  <div className="text-xs text-muted-foreground">Team</div>
                </div>
                <div
                  className={`${themeStyles.statsCard} transition-all duration-300`}
                >
                  <div className={`text-lg font-semibold mb-1`}>5k+</div>
                  <div className="text-xs text-muted-foreground">Orders</div>
                </div>
              </div>
            </div>
          </div>
          
              <p className={`text-muted-foreground text-sm pl-4 `}>
                Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.
              </p>
          {/* Join Card */}
          <div className={`${themeLayout.joinCard} mt-2`}>
            <div
              className={`${themeStyles.joinCard} transition-all duration-300`}
            >
              <h2 className={`text-lg font-semibold mb-2`}>Join the team</h2>

              <p className={`text-muted-foreground text-base mb-4`}>
                Join our affiliate program and earn rewards
              </p>
              <div className={`text-xs text-muted-foreground mb-4`}>
                10% commission on all code uses
              </div>
              <button
                className={`w-full text-white py-2 rounded-lg shadow transition-all duration-200`}
                style={{
                  backgroundColor: colors.primary,
                }}
              >
                Join Now
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
